
import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Table } from '@/components/ui/table';
import TableHeader from './table/TableHeader';
import AttendeeTableHeader from './table/AttendeeTableHeader';
import TableBody from './table/TableBody';
import { Attendee } from '@/types/attendee';
import { SortField, SortDirection } from './table/SortableTableHeader';
import { filterAttendees, sortAttendees } from './table/tableUtils';
import { useAttendeeTable } from '@/hooks/admin/useAttendeeTable';
import { Button } from '@/components/ui/button';
import { EyeIcon, EyeOffIcon } from 'lucide-react';

interface AttendeesTableProps {
  attendees: Attendee[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onDirectUpdate?: (code: string, updates: any) => Promise<any>;
  onSilentUpdate?: (attendee: Attendee) => void;
  onRefreshData?: () => void;
  lastSearches?: string[];
}

const AttendeesTable: React.FC<AttendeesTableProps> = ({
  attendees,
  searchQuery,
  setSearchQuery,
  onDirectUpdate,
  onSilentUpdate,
  onRefreshData,
  lastSearches = []
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showContribution, setShowContribution] = useState(false);
  
  const {
    sortField,
    sortDirection,
    localAttendees,
    lastUpdateTime,
    handleSort,
    handleCheckInSuccess,
    updateAttendeeLocally
  } = useAttendeeTable(attendees);

  // Apply filtering and sorting
  const filteredAttendees = filterAttendees(localAttendees, searchQuery);
  const sortedAttendees = sortAttendees(filteredAttendees, sortField, sortDirection);

  // Handle manual refresh with loading state
  const handleRefresh = () => {
    if (!onRefreshData || isRefreshing) return;
    
    setIsRefreshing(true);
    onRefreshData();
    
    // Reset refreshing state after a delay (in case the refresh is quick)
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  };

  return (
    <Card>
      <TableHeader 
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        lastUpdateTime={lastUpdateTime}
        onRefresh={onRefreshData ? handleRefresh : undefined}
        isRefreshing={isRefreshing}
        lastSearches={lastSearches}
        extraControls={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowContribution(!showContribution)}
            className="flex items-center gap-1 text-xs h-8"
          >
            {showContribution ? (
              <>
                <EyeOffIcon size={14} />
                Ẩn đóng góp
              </>
            ) : (
              <>
                <EyeIcon size={14} />
                Hiện đóng góp
              </>
            )}
          </Button>
        }
      />
      
      <Table>
        <AttendeeTableHeader
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          showContribution={showContribution}
        />
        <TableBody 
          attendees={sortedAttendees} 
          onCheckInSuccess={handleCheckInSuccess}
          onDirectUpdate={onDirectUpdate}
          onSilentUpdate={onSilentUpdate || updateAttendeeLocally}
          showContribution={showContribution}
        />
      </Table>
    </Card>
  );
};

export default AttendeesTable;
